import {WidgetRendererProps} from '@app/dashboard/biolink/biolink-editor/content/widgets/types/widget-renderer-props';
import {BusinessCardWidget} from '@app/dashboard/biolink/biolink-editor/content/widgets/business-card-widget/business-card-widget-dialog';
import {Trans} from '@common/i18n/trans';
import {PhoneIcon} from '@common/icons/material/Phone';
import {EmailIcon} from '@common/icons/material/Email';
import {LanguageIcon} from '@common/icons/material/Language';
import {PersonIcon} from '@common/icons/material/Person';
import {ReactNode} from 'react';
import {
  BusinessCardStyle,
  getBusinessCardStyle,
} from '@app/dashboard/biolink/biolink-editor/content/widgets/business-card-widget/business-card-styles';
import {
  SocialsList,
  SocialsType,
} from '@app/dashboard/biolink/biolink-editor/content/widgets/socials-widget/socials-list';
import {buildSocialUrl} from '@app/dashboard/biolink/biolink-editor/content/widgets/socials-widget/socials-widget-renderer';
import {DefaultBusinessCardTemplateId} from '@app/dashboard/biolink/biolink-editor/content/widgets/business-card-widget/business-card-templates';

type CardConfig = BusinessCardWidget['config'];
type Variant = 'editor' | 'biolinkPage';

interface TemplateProps {
  config: CardConfig;
  style: BusinessCardStyle;
  pageUrl: string;
  variant: Variant;
}

function buildVCard(config: CardConfig): string {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${config.name}`];
  if (config.company) {
    lines.push(`ORG:${config.company}`);
  }
  if (config.jobTitle) {
    lines.push(`TITLE:${config.jobTitle}`);
  }
  if (config.phone) {
    lines.push(`TEL;TYPE=WORK,VOICE:${config.phone}`);
  }
  if (config.email) {
    lines.push(`EMAIL;TYPE=WORK:${config.email}`);
  }
  if (config.website) {
    lines.push(`URL:${config.website}`);
  }
  if (config.bio) {
    lines.push(`NOTE:${config.bio.replace(/\n/g, '\\n')}`);
  }
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

function downloadVCard(config: CardConfig) {
  const vcard = buildVCard(config);
  const blob = new Blob([vcard], {type: 'text/vcard;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${config.name || 'contact'}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function websiteHref(website: string): string {
  return /^https?:\/\//.test(website) ? website : `https://${website}`;
}

export function BusinessCardWidgetRenderer({
  widget,
  variant,
}: WidgetRendererProps<BusinessCardWidget>) {
  const {config} = widget;
  const style = getBusinessCardStyle(config.style);
  const pageUrl =
    typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';
  const props: TemplateProps = {config, style, pageUrl, variant};

  switch (config.template || DefaultBusinessCardTemplateId) {
    case 'hero-photo':
      return <HeroPhotoTemplate {...props} />;
    case 'centered':
      return <CenteredTemplate {...props} />;
    case 'minimal-strip':
      return <MinimalStripTemplate {...props} />;
    case 'sidebar-split':
      return <SidebarSplitTemplate {...props} />;
    default:
      return <ClassicTemplate {...props} />;
  }
}

/* ---------------------------- shared sections ---------------------------- */

function BioText({
  config,
  className = 'border-b bg-alt px-24 py-16 text-sm',
}: {
  config: CardConfig;
  className?: string;
}) {
  if (!config.bio) return null;
  return <div className={className}>{config.bio}</div>;
}

function ContactsBlock({
  config,
  style,
  className = 'flex flex-col gap-14 px-24 py-16',
  align = 'left',
}: {
  config: CardConfig;
  style: BusinessCardStyle;
  className?: string;
  align?: 'left' | 'center';
}) {
  if (!config.phone && !config.email && !config.website) return null;
  return (
    <div className={className}>
      {config.phone && (
        <ContactRow
          icon={<PhoneIcon />}
          value={config.phone}
          href={`tel:${config.phone.replace(/[^+\d]/g, '')}`}
          accentColor={style.accentColor}
          align={align}
        />
      )}
      {config.email && (
        <ContactRow
          icon={<EmailIcon />}
          value={config.email}
          href={`mailto:${config.email}`}
          accentColor={style.accentColor}
          align={align}
        />
      )}
      {config.website && (
        <ContactRow
          icon={<LanguageIcon />}
          value={config.website}
          href={websiteHref(config.website)}
          accentColor={style.accentColor}
          align={align}
        />
      )}
    </div>
  );
}

interface ContactRowProps {
  icon: ReactNode;
  value: string;
  href: string;
  accentColor: string;
  align?: 'left' | 'center';
}
function ContactRow({
  icon,
  value,
  href,
  accentColor,
  align = 'left',
}: ContactRowProps) {
  return (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className={`flex items-center gap-14 no-underline hover:opacity-80 ${
        align === 'center' ? 'justify-center' : ''
      }`}
      style={{color: '#111827'}}
    >
      <div
        className="flex h-36 w-36 flex-shrink-0 items-center justify-center rounded-full"
        style={{backgroundColor: `${accentColor}1a`, color: accentColor}}
      >
        {icon}
      </div>
      <div className="min-w-0 truncate text-sm">{value}</div>
    </a>
  );
}

function SocialsBlock({
  config,
  style,
  className = 'flex flex-wrap gap-10 border-t px-24 py-16',
}: {
  config: CardConfig;
  style: BusinessCardStyle;
  className?: string;
}) {
  if (!config.socials || Object.keys(config.socials).length === 0) {
    return null;
  }
  return (
    <div className={className}>
      {Object.entries(config.socials).map(([type, value]) => {
        if (!value) return null;
        const social = SocialsList[type as SocialsType];
        if (!social) return null;
        const Icon = social.icon;
        return (
          <a
            key={type}
            href={buildSocialUrl(type as SocialsType, value)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-36 w-36 items-center justify-center rounded-full"
            style={{
              backgroundColor: `${style.accentColor}1a`,
              color: style.accentColor,
            }}
          >
            <Icon />
          </a>
        );
      })}
    </div>
  );
}

function LinksBlock({
  config,
  style,
  className = 'flex flex-col gap-10 border-t px-24 py-16',
}: {
  config: CardConfig;
  style: BusinessCardStyle;
  className?: string;
}) {
  if (!config.links || config.links.length === 0) return null;
  return (
    <div className={className}>
      {config.links.map((link, i) =>
        link.label && link.url ? (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-panel border py-10 text-center text-sm font-medium hover:bg-alt"
            style={{color: style.accentColor, borderColor: style.accentColor}}
          >
            {link.label}
          </a>
        ) : null,
      )}
    </div>
  );
}

function QrBlock({config, style, pageUrl, variant}: TemplateProps) {
  if (!config.showQr || variant !== 'biolinkPage' || !pageUrl) return null;
  return (
    <div className="flex flex-col items-center gap-8 border-t px-24 py-20">
      <img
        src={`${pageUrl}/qr?fg=${style.accentColor.replace('#', '')}&bg=ffffff${
          config.logo || config.photo
            ? `&logo=${encodeURIComponent(config.logo || config.photo || '')}`
            : ''
        }`}
        alt="QR code"
        className="h-120 w-120"
      />
      <div className="break-all text-center text-xs text-muted">
        {pageUrl.replace(/^https?:\/\//, '')}
      </div>
    </div>
  );
}

function AddToContactsButton({config, style, pageUrl, variant}: TemplateProps) {
  if (variant === 'biolinkPage' && pageUrl) {
    return (
      <a
        href={`${pageUrl}/vcard`}
        className="block w-full py-14 text-center text-sm font-semibold no-underline transition-opacity hover:opacity-90"
        style={{background: style.buttonBg, color: style.buttonTextColor}}
      >
        <Trans message="+ Add to Contacts" />
      </a>
    );
  }
  return (
    <button
      type="button"
      className="block w-full py-14 text-center text-sm font-semibold transition-opacity hover:opacity-90"
      style={{background: style.buttonBg, color: style.buttonTextColor}}
      onClick={() => downloadVCard(config)}
    >
      <Trans message="+ Add to Contacts" />
    </button>
  );
}

function Avatar({
  config,
  style,
  size = 64,
}: {
  config: CardConfig;
  style: BusinessCardStyle;
  size?: number;
}) {
  if (config.photo) {
    return (
      <img
        src={config.photo}
        alt=""
        className="flex-shrink-0 rounded-full object-cover"
        style={{width: size, height: size}}
      />
    );
  }
  return (
    <div
      className="flex flex-shrink-0 items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        backgroundColor: `${style.textColor}1a`,
      }}
    >
      <PersonIcon style={{color: style.textColor}} />
    </div>
  );
}

function NameBlock({
  config,
  style,
  align = 'left',
}: {
  config: CardConfig;
  style: BusinessCardStyle;
  align?: 'left' | 'center';
}) {
  return (
    <div className={align === 'center' ? 'text-center' : 'min-w-0'}>
      <div className="text-lg font-semibold">
        {config.name}
        {config.pronouns && (
          <span
            className="ml-8 text-sm font-normal"
            style={{color: style.subTextColor}}
          >
            ({config.pronouns})
          </span>
        )}
      </div>
      {config.jobTitle && (
        <div className="text-sm" style={{color: style.subTextColor}}>
          {config.jobTitle}
        </div>
      )}
      {config.company && (
        <div className="text-sm" style={{color: style.subTextColor}}>
          {config.company}
        </div>
      )}
    </div>
  );
}

function CardShell({children}: {children: ReactNode}) {
  return (
    <div className="w-full overflow-hidden rounded-panel border shadow-sm">
      {children}
    </div>
  );
}

/* -------------------------------- classic -------------------------------- */
// logo + avatar inline in a colored header, straightforward stacked body

function ClassicTemplate({config, style, pageUrl, variant}: TemplateProps) {
  return (
    <CardShell>
      <div
        className="px-24 py-24"
        style={{background: style.headerBg, color: style.textColor}}
      >
        {config.logo && (
          <img src={config.logo} alt="" className="mb-16 h-28 w-auto" />
        )}
        <div className="flex items-center gap-16">
          <Avatar config={config} style={style} />
          <NameBlock config={config} style={style} />
        </div>
      </div>

      <div style={{color: '#111827'}}>
        <BioText config={config} />
        <ContactsBlock config={config} style={style} />
        <SocialsBlock config={config} style={style} />
        <LinksBlock config={config} style={style} />
        <QrBlock config={config} style={style} pageUrl={pageUrl} variant={variant} />
      </div>

      <AddToContactsButton
        config={config}
        style={style}
        pageUrl={pageUrl}
        variant={variant}
      />
    </CardShell>
  );
}

/* ------------------------------- hero photo ------------------------------- */
// big colored header with name/title, large photo overlapping into the body

function HeroPhotoTemplate({config, style, pageUrl, variant}: TemplateProps) {
  return (
    <CardShell>
      <div
        className="relative px-24 pb-48 pt-24"
        style={{background: style.headerBg, color: style.textColor}}
      >
        {config.logo && (
          <img src={config.logo} alt="" className="mb-20 h-24 w-auto" />
        )}
        <div className="text-2xl font-bold leading-tight">{config.name}</div>
        {config.pronouns && (
          <div
            className="text-base italic"
            style={{color: style.subTextColor}}
          >
            ({config.pronouns})
          </div>
        )}
        {config.jobTitle && (
          <div
            className="mt-12 text-sm font-medium"
            style={{color: style.subTextColor}}
          >
            {config.jobTitle}
          </div>
        )}
        {config.company && (
          <div className="text-sm" style={{color: style.subTextColor}}>
            {config.company}
          </div>
        )}
        <div className="absolute bottom-[-40px] right-24">
          <div className="rounded-full border-4 border-white">
            <Avatar config={config} style={style} size={88} />
          </div>
        </div>
      </div>

      <div style={{color: '#111827'}} className="pt-24">
        <BioText config={config} className="border-b bg-alt px-24 py-16 text-sm" />
        <ContactsBlock config={config} style={style} />
        <SocialsBlock config={config} style={style} />
        <LinksBlock config={config} style={style} />
        <QrBlock config={config} style={style} pageUrl={pageUrl} variant={variant} />
      </div>

      <AddToContactsButton
        config={config}
        style={style}
        pageUrl={pageUrl}
        variant={variant}
      />
    </CardShell>
  );
}

/* --------------------------------- centered -------------------------------- */
// no colored block, just a thin accent top border, everything centered

function CenteredTemplate({config, style, pageUrl, variant}: TemplateProps) {
  return (
    <CardShell>
      <div style={{height: 6, background: style.accentColor}} />
      <div
        className="flex flex-col items-center px-24 py-32 text-center"
        style={{color: '#111827'}}
      >
        <Avatar config={config} style={{...style, textColor: '#9ca3af'}} size={96} />
        <div className="mt-16 text-lg font-semibold">
          {config.name}
          {config.pronouns && (
            <span className="ml-8 text-sm font-normal text-muted">
              ({config.pronouns})
            </span>
          )}
        </div>
        {config.jobTitle && (
          <div className="text-sm text-muted">{config.jobTitle}</div>
        )}
        {config.company && (
          <div className="text-sm text-muted">{config.company}</div>
        )}
      </div>

      <BioText
        config={config}
        className="border-y bg-alt px-24 py-16 text-center text-sm"
      />
      <ContactsBlock
        config={config}
        style={style}
        align="center"
        className="flex flex-col items-center gap-14 px-24 py-16"
      />
      <SocialsBlock
        config={config}
        style={style}
        className="flex flex-wrap justify-center gap-10 border-t px-24 py-16"
      />
      <LinksBlock config={config} style={style} />
      <QrBlock config={config} style={style} pageUrl={pageUrl} variant={variant} />

      <AddToContactsButton
        config={config}
        style={style}
        pageUrl={pageUrl}
        variant={variant}
      />
    </CardShell>
  );
}

/* ------------------------------ minimal strip ------------------------------ */
// thin accent bar only, plain white card, compact spacing

function MinimalStripTemplate({config, style, pageUrl, variant}: TemplateProps) {
  return (
    <CardShell>
      <div style={{height: 8, background: style.accentColor}} />
      <div className="flex items-center gap-14 px-20 py-20" style={{color: '#111827'}}>
        <Avatar config={config} style={{...style, textColor: '#9ca3af'}} size={48} />
        <div className="min-w-0">
          <div className="text-base font-semibold">
            {config.name}
            {config.pronouns && (
              <span className="ml-6 text-xs font-normal text-muted">
                ({config.pronouns})
              </span>
            )}
          </div>
          <div className="truncate text-xs text-muted">
            {[config.jobTitle, config.company].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      <div style={{color: '#111827'}}>
        <BioText config={config} className="border-t px-20 py-12 text-sm" />
        <ContactsBlock
          config={config}
          style={style}
          className="flex flex-col gap-10 border-t px-20 py-12"
        />
        <SocialsBlock
          config={config}
          style={style}
          className="flex flex-wrap gap-8 border-t px-20 py-12"
        />
        <LinksBlock
          config={config}
          style={style}
          className="flex flex-col gap-8 border-t px-20 py-12"
        />
        <QrBlock config={config} style={style} pageUrl={pageUrl} variant={variant} />
      </div>

      <AddToContactsButton
        config={config}
        style={style}
        pageUrl={pageUrl}
        variant={variant}
      />
    </CardShell>
  );
}

/* ------------------------------ sidebar split ------------------------------ */
// colored sidebar (photo/name/QR) next to the contact details; stacks on mobile

function SidebarSplitTemplate({config, style, pageUrl, variant}: TemplateProps) {
  return (
    <CardShell>
      <div className="flex flex-col md:flex-row">
        <div
          className="flex flex-col items-center gap-12 px-24 py-32 text-center md:w-[38%]"
          style={{background: style.headerBg, color: style.textColor}}
        >
          {config.logo && (
            <img src={config.logo} alt="" className="h-24 w-auto" />
          )}
          <Avatar config={config} style={style} size={80} />
          <div>
            <div className="text-base font-semibold">{config.name}</div>
            {config.jobTitle && (
              <div className="text-xs" style={{color: style.subTextColor}}>
                {config.jobTitle}
              </div>
            )}
            {config.company && (
              <div className="text-xs" style={{color: style.subTextColor}}>
                {config.company}
              </div>
            )}
          </div>
          {config.showQr && variant === 'biolinkPage' && pageUrl && (
            <img
              src={`${pageUrl}/qr?fg=${style.accentColor.replace('#', '')}&bg=ffffff${
                config.logo || config.photo
                  ? `&logo=${encodeURIComponent(config.logo || config.photo || '')}`
                  : ''
              }`}
              alt="QR code"
              className="mt-8 h-84 w-84 rounded bg-white p-4"
            />
          )}
        </div>

        <div className="flex-auto" style={{color: '#111827'}}>
          <BioText config={config} className="border-b bg-alt px-24 py-16 text-sm" />
          <ContactsBlock config={config} style={style} />
          <SocialsBlock config={config} style={style} />
          <LinksBlock config={config} style={style} />
        </div>
      </div>

      <AddToContactsButton
        config={config}
        style={style}
        pageUrl={pageUrl}
        variant={variant}
      />
    </CardShell>
  );
}
